/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';
import { Button, Form, Header, Icon, Modal } from '@mairistem/composants';

import { editions } from '@/routes';

interface ModalEditionRecensementProps {
  identifiant: string | number;
  nomFamille: string;
  prenom: string;
  onClose: () => void;
}

const ModalEditionRecensement = (props: ModalEditionRecensementProps) => {
  const { identifiant, nomFamille, prenom, onClose } = props;

  const [noticeEnabled, setNoticeEnabled] = React.useState<boolean>(false);
  const [attestationEnabled, setAttestationEnabled] =
    React.useState<boolean>(false);
  const [data, setData] = React.useState({
    editeDateAttestation: false,
    editeDateNotice: false,
    preImprime: false,
    margeHaute: 0,
    margeGauche: 0,
  });

  const [loading, setLoading] = React.useState<boolean>(false);

  const handleChange = React.useCallback(
    (
      _e: React.FormEvent<HTMLInputElement>,
      {
        name,
        value,
        checked,
      }: { name: string; value: unknown; checked?: boolean },
    ) => {
      setData((prev) => ({
        ...prev,
        [name]: checked ?? value,
      }));
    },
    [],
  );

  const handlePrint = React.useCallback(() => {
    setLoading(true);

    const body = {
      editeDateAttestation: !data?.editeDateAttestation,
      editeDateNotice: !data?.editeDateNotice,
      preImprime: data?.preImprime,
      margeHaute: data?.margeHaute,
      margeGauche: data?.margeGauche,
      attestation: attestationEnabled,
      notice: noticeEnabled,
    };

    request
      .post(editions(identifiant), body, {
        headers: { Accept: '*/*' },
        responseType: 'blob',
      })
      .then((response: any) => {
        // Get Content-Type from response
        const contentType = response.type || '';

        // Create a URL for the blob
        const url = URL.createObjectURL(response);

        if (contentType.includes('application/pdf')) {
          const blob = response.data || response;

          // Créer un nouveau blob avec le bon type
          const pdfBlob = new Blob([blob], { type: 'application/pdf' });

          // Créer un File object avec le bon nom
          const url2 = URL.createObjectURL(pdfBlob);

          // If PDF, open in new tab
          window.open(url2, '_blank');
        } else if (contentType.includes('application/zip')) {
          // If ZIP, trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = `edition_${nomFamille}_${prenom}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          toast('Format de fichier non reconnu', { type: 'error' });
        }

        // Clean up URL object after a short delay
        // setTimeout(() => URL.revokeObjectURL(url), 100);
      })
      .catch(() => {
        toast("Une erreur est survenue lors de l'édition des documents", {
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    attestationEnabled,
    data?.editeDateAttestation,
    data?.editeDateNotice,
    data?.margeGauche,
    data?.margeHaute,
    data?.preImprime,
    identifiant,
    nomFamille,
    noticeEnabled,
    prenom,
  ]);

  return (
    <Modal
      open
      onClose={onClose}
      data-testid="modal-editions-recensement"
      style={{ width: '900px' }}
    >
      <Modal.Header>
        <Icon name="print" />
        Edition des documents de recensement
      </Modal.Header>
      <Modal.Content>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            gap: '.5em',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
            <Form.Checkbox
              data-testid="modal-editions-recensement-checkbox-notice"
              value={noticeEnabled}
              onChange={() => setNoticeEnabled((prev) => !prev)}
            />
            <Header as="h3" disabled={!noticeEnabled} style={{ margin: 0 }}>
              Notice Individuelle
            </Header>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1em',
              paddingLeft: '30px',
            }}
          >
            <Form.Checkbox
              name="editeDateNotice"
              data-testid="modal-editions-recensement-checkbox-editedate"
              onChange={handleChange}
              checked={data?.editeDateNotice}
              disabled={!noticeEnabled}
              label="Ne pas afficher les dates d'édition"
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1em',
              paddingTop: '2em',
            }}
          >
            <Form.Checkbox
              data-testid="modal-editions-recensement-checkbox-attestation"
              value={attestationEnabled}
              onChange={() => setAttestationEnabled((prev) => !prev)}
            />
            <Header
              as="h3"
              disabled={!attestationEnabled}
              style={{ margin: 0 }}
            >
              Attestation de recensement
            </Header>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1em',
              paddingLeft: '30px',
            }}
          >
            <Form.Checkbox
              name="editeDateAttestation"
              data-testid="modal-editions-recensement-checkbox-editedate"
              onChange={handleChange}
              checked={data?.editeDateAttestation}
              disabled={!attestationEnabled}
              label="Ne pas afficher les dates d'édition"
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1em',
              paddingLeft: '30px',
              paddingTop: 0,
            }}
          >
            <Form.Checkbox
              name="preImprime"
              data-testid="modal-editions-recensement-checkbox-preimprime"
              onChange={handleChange}
              checked={data?.preImprime}
              disabled={!attestationEnabled}
              label="Utilisation d'un pré-imprimé en utilisant une marge haute"
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5em',
                opacity: data?.preImprime ? 1 : 0.5,
              }}
            >
              <Form.Input
                name="margeHaute"
                value={data?.margeHaute}
                onChange={handleChange}
                data-testid="modal-editions-recensement-input-marge-haute"
                disabled={!attestationEnabled || !data?.preImprime}
                style={{ width: 50 }}
              />
              (+-mm)
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5em',
                opacity: data?.preImprime ? 1 : 0.5,
              }}
            >
              <span style={{ width: 140 }}>et une marge gauche</span>
              <Form.Input
                name="margeGauche"
                value={data?.margeGauche}
                onChange={handleChange}
                data-testid="modal-editions-recensement-input-marge-gauche"
                disabled={!attestationEnabled || !data?.preImprime}
                style={{ width: 50 }}
              />
              (+-mm)
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          size="tiny"
          icon="cancel"
          content="Annuler"
          data-testid="modal-editions-recensement-close"
          disabled={loading}
          loading={loading}
          onClick={onClose}
        />
        <Button
          data-testid="modal-editions-recensement-bouton-imprimer"
          positive
          icon="print"
          content="Imprimer"
          loading={loading}
          disabled={loading || (!noticeEnabled && !attestationEnabled)}
          onClick={handlePrint}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalEditionRecensement;
